export { COOKIE_NAME, ONE_YEAR_MS } from "@shared/const";

// Check if running in local dev mode (no Auth0 domain configured)
const isLocalDevMode = () => {
  const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
  return !auth0Domain || auth0Domain === "";
};

/**
 * Generate Auth0 login URL at runtime so redirect URI reflects the current origin.
 * Uses Auth0's /authorize endpoint with standard OAuth2/OIDC parameters.
 * @param returnTo - Optional path to redirect to after login (e.g. "/secrets")
 */
export const getLoginUrl = (returnTo?: string) => {
  // In local dev mode, use the dev-login endpoint
  if (isLocalDevMode()) {
    const devUrl = `${window.location.origin}/api/dev-login`;
    if (returnTo) {
      return `${devUrl}?returnTo=${encodeURIComponent(returnTo)}`;
    }
    return devUrl;
  }

  const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE || "";
  const redirectUri = `${window.location.origin}/api/oauth/callback`;

  // Encode redirect URI and optional returnTo path in state
  const statePayload = returnTo
    ? JSON.stringify({ redirectUri, returnTo })
    : redirectUri;
  const state = btoa(statePayload);

  const url = new URL(`https://${auth0Domain}/authorize`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("scope", "openid profile email");
  url.searchParams.set("state", state);

  // Optional: Set audience if using Auth0 API authorization
  if (audience) {
    url.searchParams.set("audience", audience);
  }

  return url.toString();
};

/**
 * Generate Auth0 logout URL
 * Clears the Auth0 session and redirects back to the app
 */
export const getLogoutUrl = () => {
  if (isLocalDevMode()) {
    return `${window.location.origin}/`;
  }

  const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const returnTo = window.location.origin;

  const url = new URL(`https://${auth0Domain}/v2/logout`);
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("returnTo", returnTo);

  return url.toString();
};
